import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { GraduationCap } from "lucide-react";
import { ALLOWED_EMAIL_DOMAINS } from "./config";

// True if the given email address's domain matches one of ALLOWED_EMAIL_DOMAINS
// (exact match, or a subdomain of one).
function isAllowedEmailDomain(email) {
  const domain = email.trim().toLowerCase().split("@")[1];
  if (!domain) return false;
  return ALLOWED_EMAIL_DOMAINS.some(
    (allowed) => domain === allowed || domain.endsWith("." + allowed)
  );
}

// Split-screen auth shell: a brand/pitch panel beside the form card.
function AuthShell({ children }) {
  return (
    <div className="flex min-h-screen bg-canvas">
      {/* Brand / pitch panel (hidden on small screens) */}
      <div className="relative hidden w-[44%] flex-col justify-between overflow-hidden bg-primary p-12 text-white lg:flex">
        {/* decorative course-map node cluster */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.18]">
          <div className="absolute left-[18%] top-[24%] h-24 w-24 rounded-full bg-white blur-2xl" />
          <div className="absolute left-[58%] top-[40%] h-32 w-32 rounded-full bg-accent blur-3xl" />
          <div className="absolute left-[34%] top-[66%] h-20 w-20 rounded-full bg-primary-light blur-2xl" />
        </div>
        <div className="relative flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/15">
            <GraduationCap className="h-6 w-6" />
          </span>
          <span className="font-display text-lg font-semibold">Courstellation</span>
        </div>
        <div className="relative">
          <h2 className="font-display text-4xl font-semibold leading-tight text-white">
            <strong>Courstellation</strong>lance.
          </h2>
          <p className="mt-4 max-w-sm text-base leading-relaxed text-white/75">
            Explore every course as an interactive map where related ideas sit
            close together — and discover the paths you haven't taken yet.
          </p>
        </div>
        <p className="relative text-sm text-white/55">
          Built by volunteers · Free for students
        </p>
      </div>

      {/* Form side */}
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}

export default function Auth({ onLogin, onShowUserInfo }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState(""); // message state
  const [debugInfo, setDebugInfo] = useState(""); // debug info state
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [isSignUpMode, setIsSignUpMode] = useState(false);

  const checkUserInfo = async (user) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        console.error("No valid session token found");
        return false;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/check_user_info`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error("Failed to check user info");
        return false;
      }

      const data = await response.json();
      return data.has_info;
    } catch (error) {
      console.error("Error checking user info:", error);
      return false;
    }
  };

  const saveUserInfo = async (userInfo) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("No valid session token found");
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/save_user_info`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(userInfo),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to save user information");
      }

      return await response.json();
    } catch (error) {
      console.error("Error saving user info:", error);
      throw error;
    }
  };

  const signUp = async () => {
    setMessage("");
    setDebugInfo(`SignUp Attempt - Email: ${email}`);

    if (!isAllowedEmailDomain(email)) {
      const domainList = ALLOWED_EMAIL_DOMAINS.map((d) => `@${d}`).join(" or ");
      setMessage(`Sign-up is limited to ${domainList} email addresses.`);
      return;
    }

    const { data, error: signUpError } = await supabase.auth.signUp({ email, password });

    if (!signUpError) {
      // Supabase's email enumeration protection returns a dummy user with no identities if the email already exists
      if (data?.user?.identities != null && data.user.identities.length === 0) {
        setMessage("This email is already registered. Please sign in.");
      } else {
        setMessage("Check your email to confirm your sign-up!");
      }
    } else if (signUpError.message.includes("User already registered")) {
      setMessage("This email is already registered. Please sign in.");
    } else {
      console.error("SignUp Error:", signUpError);
      setMessage(
        `Error: ${signUpError.message}` +
        (signUpError.code ? ` (Code: ${signUpError.code})` : "") +
        (signUpError.status ? ` (Status: ${signUpError.status})` : "")
      );
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setMessage("Please enter your email to reset your password.");
      return;
    }

    setMessage("");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}`,
    });

    if (error) {
      console.error("Reset Password Error:", error);
      if (error.message.includes("rate limit exceeded")) {
        setMessage("Email rate limit exceeded. Please wait a minute or two before trying again.");
      } else {
        setMessage(`Error: ${error.message}`);
      }
    } else {
      setResetSent(true);
      setMessage("Check your email for the password reset link!");
    }
  };

  if (isResetMode) {
    return (
      <AuthShell>
        <div className="card p-8">
          <h1 className="font-display text-2xl font-semibold text-ink">Reset password</h1>
          <p className="mt-1 mb-6 text-sm text-ink-muted">
            Enter your email and we'll send you a recovery link.
          </p>

          <input
            className="input"
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <button
            onClick={handleResetPassword}
            disabled={resetSent}
            className="btn-primary mt-4 w-full"
          >
            {resetSent ? "Email sent" : "Send recovery link"}
          </button>

          <button
            onClick={() => {
              setIsResetMode(false);
              setResetSent(false);
              setMessage("");
            }}
            className="mt-5 w-full text-sm font-medium text-primary hover:underline"
          >
            Back to sign in
          </button>

          {message && (
            <div
              className={`mt-4 rounded-lg border p-3 text-sm ${
                resetSent
                  ? "border-success/30 bg-success-soft text-success"
                  : "border-danger/30 bg-danger-soft text-danger"
              }`}
            >
              {message}
            </div>
          )}
        </div>
      </AuthShell>
    );
  }

  const signIn = async () => {
    setMessage("");
    const debugText = `SignIn Attempt - Email: ${email}, Password: ${password}`;
    setDebugInfo(debugText);
    // console.log(debugText);

    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (!signInError) {
      if (data.user && !data.user.confirmed_at) {
        setMessage("Please confirm your email before signing in.");
      } else {
        setMessage("");
        // Just proceed with login - App.js will handle checking for user info
        onLogin(data.user);
      }
    } else {
      console.error("SignIn Error:", signInError);
      if (signInError.message.includes("Invalid login credentials")) {
        setMessage(
          `Invalid email or password.` +
          (signInError.code ? ` (Code: ${signInError.code})` : "") +
          (signInError.status ? ` (Status: ${signInError.status})` : "")
        );
      } else {
        setMessage(
          `Error: ${signInError.message}` +
          (signInError.code ? ` (Code: ${signInError.code})` : "") +
          (signInError.status ? ` (Status: ${signInError.status})` : "")
        );
      }
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") (isSignUpMode ? signUp : signIn)();
  };

  return (
    <AuthShell>
      <div className="card p-8">
        {/* Header */}
        <div className="mb-7">
          <h1 className="font-display text-2xl font-semibold text-ink">
            {isSignUpMode ? "Create your account" : "Welcome back"}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            {isSignUpMode
              ? "Start mapping your academic path."
              : "Sign in to explore your course map."}
          </p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              className="input"
              type="email"
              placeholder="you@college.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={onKeyDown}
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="label" htmlFor="auth-password">Password</label>
              {!isSignUpMode && (
                <button
                  onClick={() => setIsResetMode(true)}
                  className="mb-1.5 text-xs font-medium text-primary hover:underline"
                >
                  Forgot password?
                </button>
              )}
            </div>
            <input
              id="auth-password"
              className="input"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>

        {/* Button */}
        <button
          onClick={isSignUpMode ? signUp : signIn}
          className="btn-primary mt-6 w-full"
        >
          {isSignUpMode ? "Sign up" : "Sign in"}
        </button>

        {/* Toggle Mode */}
        <p className="mt-6 text-center text-sm text-ink-muted">
          {isSignUpMode ? "Already have an account?" : "Don't have an account?"}{" "}
          <button
            onClick={() => {
              setIsSignUpMode(!isSignUpMode);
              setMessage("");
            }}
            className="font-semibold text-primary hover:underline"
          >
            {isSignUpMode ? "Sign in" : "Sign up"}
          </button>
        </p>

        {/* Message */}
        {message && (
          <div
            className={`mt-4 rounded-lg border p-3 text-sm ${
              message.includes("Error") || message.includes("Invalid")
                ? "border-danger/30 bg-danger-soft text-danger"
                : "border-primary/20 bg-primary-50 text-primary"
            }`}
          >
            {message}
          </div>
        )}
      </div>
    </AuthShell>
  );
}
